import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/dist/client/router';

import { Field, LinkField, Text, useSitecoreContext, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';

import { FetchState } from 'src/enums/FetchState';

import useSearchParams from 'src/hooks/useSearchParams';

import { ComponentProps, ComponentWithContextProps, SitecoreContextValue } from 'lib/ComponentProps';

import { mapFilterGroup } from 'src/mappers/models/filterGroupMapper';
import { mapResourceListItem } from 'src/mappers/models/resouceListItemMapper';

import { SearchRequestData } from 'src/services/search/SearchDtos';
import { searchRequest } from 'src/services/search/searchRequest';

import { ExtendedItem } from 'src/types/ExtendedItem';
import { ResourceListItem } from 'src/types/ResourceListItem';
import { SocialSharingOptions } from 'src/types/SocialSharingOptions';
import { TagItem } from 'src/types/TagItem';

import addTitleToGuid from 'src/utils/addTitleToGuid';
import debounce from 'src/utils/debounce';
import { toArray } from 'src/utils/helpers';
import IsSitecoreEditing from 'src/utils/isSitecoreEditing';
import parseGuids from 'src/utils/parseGuids';
import stripHtml from 'src/utils/stripHtml';

import Accordion from 'src/components/basicContent/_Accordion/Accordion';
import { Button } from 'src/components/basicContent/_Button/Button';
import WildCardFields from 'src/components/contentOverview/WildCardFields';
import GaSearchScripts from 'src/components/global/_Gtm/_GaSearchScripts';
import { Listing } from 'src/components/search/_Listing/_Listing';

import ChevronDownSvg from 'public/icons/ChevronDown.svg';
import CloseIconSvg from 'public/icons/close.svg';

import { ClearAll } from '../_ClearAll/_ClearAll';
import { SearchFilter } from '../_SearchFilter/_SearchFilter';
import { SearchListingLoading } from '../_SearchListingLoading/SearchListingLoading';
import { ShowMore } from '../_ShowMore/_ShowMore';
import { SortByFilter } from '../_SortBy/_SortByFilter';

import styles from './SearchListing.module.css';

type SearchListingProps = ComponentWithContextProps &
	ComponentProps & {
		fields: WildCardFields & {
			FoundResultsText: Field<string>;
			RefineSearchText: Field<string>;
			ClearFiltersText: Field<string>;
			SortByText: Field<string>;
			SortOptions: ExtendedItem &
				[
					{
						fields: {
							'Id Value': Field<number>;
							Title: Field<string>;
						};
					}
				];
			ContentTypes: ExtendedItem &
				[
					{
						fields: {
							Template: ExtendedItem;
						};
					}
				];
			ShowMoreText: Field<string>;
			CurrentlyShowingText: Field<string>;
			FilterGroups: ExtendedItem[];
			FavouriteButtonText: Field<string>;
			UnfavouriteText: Field<string>;
			NoResultsFoundText: Field<string>;
			ResultsPerPage: Field<string>;
			SearchPlaceholderText: Field<string>;
			SearchDescription: Field<string>;
			WildCardConfig: ExtendedItem & {
				fields: {
					SearchListingWildcardPage: LinkField;
				};
			};
			RegisteredUserText: Field<string>;
			RCNMemberText: Field<string>;
			RCNiPlusSubscriberText: Field<string>;
			OrganisationalProductText: Field<string>;
			OpenAccessText: Field<string>;
			SubjectLinkPrependedText: Field<string>;
			CPDHoursText: Field<string>;
			ShareButtonToolTip: Field<string>;
			ShareOptions: SocialSharingOptions;
			NoShareOptions: Field<string>;
			Tags: TagItem[];
		};
	};

export default withDatasourceCheck()<SearchListingProps>(SearchListing);

function SearchListing(props: SearchListingProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const router = useRouter();
	const params = useSearchParams({
		content: props.fields.ContentTypes?.map((t) => t.fields.Template.id),
		pageSize: Number(props.fields.ResultsPerPage?.value) ?? 24,
	});
	const isEditing = IsSitecoreEditing();

	const formMethods = useForm<SearchRequestData>({
		defaultValues: {
			Keyword: stripHtml(params.q),
			PageSize: params.pageSize,
			SortOption: params.sortOption,
			LearningTopics: toArray(params.topics),
			TypeOfAccess: toArray(params.access),
			TypeOfResources: toArray(params.resources),
			TypeOfLearning: toArray(params.learning),
			ContentTypes: toArray(params.content),
			NursingRoles: toArray(params.roles),
			RcniLearningCategories: toArray(params.rcniCategories),
		},
	});

	const formRef = useRef<HTMLFormElement>(null);

	const [searchState, setSearchState] = useState(FetchState.START);
	const [searchInitialised, setSearchInitialised] = useState(false);
	const [totalResults, setTotalResults] = useState(0);
	const [pageIndex, setPageIndex] = useState(params.pageIndex);

	const [scrollY, setScrollY] = useState(params.scrollY);

	const [spellingSuggestion, setSpellingSuggestion] = useState<string | null>(null);
	const [searchResult, setSearchResult] = useState<ResourceListItem[]>([]);
	const [previousResults, setPreviousResults] = useState<ResourceListItem[]>([]);
	const [prevKeyword, setPrevKeyword] = useState<string>('');

	const sitecoreContextTags = sitecoreContext.route.fields?.Tags as unknown as TagItem[];
	const tags = props.fields.FilterGroups.map((field) => field.fields)
		.flatMap((tag) => tag.Options)
		.filter((tagItem: TagItem) => tagItem?.name.includes(`${sitecoreContextTags?.[0].name}`)) as TagItem[];

	const [learningTopics, setLearningTopics] = useState(
		tags.length > 0 ? tags?.[0]?.name.split('-')[1].replaceAll(' ', '') + '_' + tags?.[0]?.id : ''
	);

	useEffect(() => {
		if (tags.length > 0) {
			setLearningTopics(tags[0].name.split('-')[1].replaceAll(' ', '') + '_' + tags[0].id);
		}
	}, []);

	useEffect(() => {
		const currentKeyword = formMethods.getValues('Keyword') || '';

		if (prevKeyword === '' || currentKeyword !== prevKeyword) {
			setPrevKeyword(currentKeyword); // Update the previous keyword
		}
	}, [formMethods.getValues('Keyword')]);

	const search: SubmitHandler<SearchRequestData> = useCallback(
		debounce(async (searchFormData: SearchRequestData) => {
			if (isEditing) {
				return;
			}

			setSearchState(FetchState.LOADING);

			// Only reset PageIndex if the keyword has changed and prevKeyword is not the initial empty string
			if (searchFormData.Keyword !== prevKeyword && prevKeyword !== '') {
				setPageIndex(1);
				setPreviousResults([]);
				formMethods.setValue('PageIndex', 1);
			}

			if (learningTopics) {
				setLearningTopics('');
			}
			// Update prevKeyword after performing the check
			setPrevKeyword(searchFormData.Keyword || '');

			// Continue with search process...
			searchFormData.Keyword = searchFormData.Keyword ? stripHtml(searchFormData.Keyword) : '';
			searchFormData.PageSize = searchFormData.PageSize ?? params.pageSize;
			searchFormData.SortOption = searchFormData.SortOption ?? params.sortOption;
			searchFormData.LearningTopics = toArray(learningTopics.trim() !== '' ? learningTopics : searchFormData.LearningTopics);
			searchFormData.TypeOfAccess = toArray(searchFormData.TypeOfAccess);
			searchFormData.TypeOfResources = toArray(searchFormData.TypeOfResources);
			searchFormData.TypeOfLearning = toArray(searchFormData.TypeOfLearning);
			searchFormData.NursingRoles = toArray(searchFormData.NursingRoles);
			searchFormData.RcniLearningCategories = toArray(searchFormData.RcniLearningCategories);
			searchFormData.ContentTypes = toArray(searchFormData.ContentTypes);

			// Update the URL with new query parameters
			router.push(
				{
					pathname: router.asPath.split('?')[0],
					query: {
						q: searchFormData.Keyword,
						pageIndex: pageIndex > Number(searchFormData.PageIndex) ? pageIndex : searchFormData.PageIndex,
						topics: searchFormData.LearningTopics?.join(','),
						access: searchFormData.TypeOfAccess?.join(','),
						resources: searchFormData.TypeOfResources?.join(','),
						learning: searchFormData.TypeOfLearning?.join(','),
						roles: searchFormData.NursingRoles?.join(','),
						rcniCategories: searchFormData.RcniLearningCategories?.join(','),
						content: searchFormData.ContentTypes?.join(','),
						pageSize: params.pageSize,
						sortOption: searchFormData.SortOption,
						scrollY: scrollY,
					},
				},
				undefined,
				{ shallow: true }
			);

			searchFormData.LearningTopics = parseGuids(learningTopics.trim() !== '' ? [learningTopics] : searchFormData.LearningTopics);
			searchFormData.TypeOfAccess = parseGuids(searchFormData.TypeOfAccess);
			searchFormData.TypeOfResources = parseGuids(searchFormData.TypeOfResources);
			searchFormData.TypeOfLearning = parseGuids(searchFormData.TypeOfLearning);
			searchFormData.NursingRoles = parseGuids(searchFormData.NursingRoles);
			searchFormData.RcniLearningCategories = parseGuids(searchFormData.RcniLearningCategories);
			searchFormData.ContentTypes = parseGuids(searchFormData.ContentTypes);

			// Make the search request
			const { data, state } = await searchRequest(searchFormData);

			// Update state with search results
			setTotalResults(data?.TotalResults || 0);
			setSpellingSuggestion(data?.SpellingCollations?.[0] || null);

			// Map the results
			const list = Array<ResourceListItem>();
			if (data?.Results?.length) {
				data.Results.map((result) => {
					const item = mapResourceListItem(result, props.fields.WildCardConfig?.fields?.SearchListingWildcardPage?.value?.href);
					if (item) {
						list.push(item);
					}
				});
			}

			setSearchResult(list);
			setSearchState(state);
			!searchInitialised && setSearchInitialised(true);
		}, 300),
		[pageIndex, scrollY, prevKeyword] // Make sure prevKeyword is included in dependencies
	);

	useEffect(() => {
		const q = stripHtml(params.q);
		if (formMethods.getValues('Keyword') !== q) {
			formMethods.setValue('Keyword', q);
		}

		if (!searchInitialised || (searchInitialised && params.fromStandaloneSearch)) {
			search({
				Keyword: q,
				LearningTopics: params.topics,
				TypeOfAccess: params.access,
				TypeOfResources: params.resources,
				TypeOfLearning: params.learning,
				NursingRoles: params.roles,
				RcniLearningCategories: params.rcniCategories,
				ContentTypes: params.content,
				PageIndex: 1,
				PageSize: params.pageSize * pageIndex,
				SortOption: params.sortOption,
			});
		}
	}, [params.fromStandaloneSearch]);

	useEffect(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const subscription = formMethods.watch(formMethods.handleSubmit(search));

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		return () => subscription.unsubscribe();
	}, [formMethods.handleSubmit, search, formMethods.watch, router]);

	function nextPage() {
		setPreviousResults([...previousResults, ...searchResult]);
		setSearchResult([]);
		formMethods.setValue('PageIndex', pageIndex + 1);
		setPageIndex(pageIndex + 1);
	}

	const filterGroups = mapFilterGroup(props.fields.FilterGroups);

	const searchFilterData = useMemo(() => {
		return filterGroups?.map((filterGroup) => {
			const checkboxGroupName = filterGroup.dtoPropertyName;

			if (!checkboxGroupName) {
				console.error(`The DtoPropertyName field does not have a value on ${filterGroup.id} so unable to display checkbox group`);
			}

			return {
				name: checkboxGroupName,
				placeholder: filterGroup.title,
				data: filterGroup.options.map((option) => {
					const titleArray = option?.title.split('—');

					return {
						label: (
							<>
								<strong>{titleArray[0]}</strong>

								{titleArray[1]}
							</>
						),
						value: addTitleToGuid(option),
					};
				}),
			};
		});
	}, []);

	const sortByData = () => {
		return props.fields.SortOptions.map((x) => {
			const value = (x.fields['Id Value'] as Field<number>)?.value;
			const title = x.fields.Title?.value;

			return {
				value: value,
				label: title,
			};
		});
	};

	const keywordResultsText = props.fields?.FoundResultsText?.value.replace('$count$', `${totalResults}`);
	const generalResultsText = `${props.fields?.FoundResultsText?.value.split('$count$')[0]} ${totalResults} results.`;

	useEffect(() => {
		const events = ['scroll', 'resize'];

		let abortDebounce = false;

		const debounceScrollStateUpdate = debounce(() => {
			if (abortDebounce) {
				return;
			}

			setScrollY(Math.round(window.pageYOffset));
		}, 500);

		events.forEach((event) => window.addEventListener(event, debounceScrollStateUpdate, true));

		return () => {
			events.forEach((event) => window.removeEventListener(event, debounceScrollStateUpdate, true));

			abortDebounce = true;
		};
	}, []);

	useEffect(() => {
		if (isEditing) {
			return;
		}
		const { path: _, ...routerQueries } = router.query;

		router.replace(
			{
				pathname: router.asPath.split('?')[0],
				query: {
					...routerQueries,
					scrollY: scrollY,
				},
			},
			undefined,
			{
				shallow: true,
			}
		);
	}, [scrollY, isEditing]);

	useEffect(() => {
		if (!searchInitialised) {
			return;
		}

		window.scrollTo({ top: scrollY, left: 0, behavior: 'smooth' });
	}, [searchInitialised]);

	useEffect(() => {
		const keywordInput = formRef.current?.querySelector('input[id="search-input"]') as HTMLInputElement | null;

		if (keywordInput && formMethods?.getValues('Keyword')?.trim() !== '') {
			keywordInput.focus();
		}
	}, [formRef, formMethods.getValues, params.q, router, formMethods.setValue]);

	return (
		<>
			<GaSearchScripts
				totalResults={totalResults}
				q={stripHtml(params.q)}
				topics={params.topics}
				access={params.access}
				learning={params.learning}
				resources={params.resources}
			/>

			<section className={styles.section + ' maxWidth'}>
				<FormProvider {...formMethods}>
					<form ref={formRef} className={styles.controls} onSubmit={(e) => e.preventDefault()}>
						<div className={styles.filterSection}>
							<section className={styles.filterRow}>
								<h4 className={styles.filterTitle}>Search</h4>
								<label htmlFor="search-input" className="screenReaderOnly">
									<Text field={props.fields.SearchDescription} />
								</label>
								<div
									className={`${styles.searchBarWrapper} ${
										formMethods.getValues('Keyword') && (formMethods.getValues('Keyword') as string).length > 0 ? styles.active : ''
									}`}
								>
									<input
										type="search"
										id="search-input"
										autoComplete="false"
										data-hj-allow
										className={styles.searchBar}
										placeholder={props.fields?.SearchPlaceholderText?.value}
										defaultValue={params.q}
										autoFocus
										{...formMethods.register('Keyword', {
											minLength: 3,
										})}
									/>

									{formMethods.getValues('Keyword') && (formMethods.getValues('Keyword') as string).length > 0 && (
										<Button title="Close Icon" className={styles.clearKeyword} onClick={() => formMethods.setValue('Keyword', '')}>
											<CloseIconSvg className={styles.clearKeywordIcon} />
										</Button>
									)}
								</div>
							</section>

							<Accordion
								sectionClass={styles.filterRow}
								headerClass={styles.filterAccordionHeadWrapper}
								bodyClass={styles.filterAccordionBody}
								startOpen
								activeEnd={799}
							>
								<div className={styles.filterAccordionHead}>
									<Text field={props.fields.RefineSearchText} tag="h4" className={styles.filterTitle} />

									<ChevronDownSvg className={styles.filterAccordionChevron} />
								</div>

								<section data-filter-container className={`${styles.filterRow}`}>
									{searchFilterData?.map((data, index) => {
										return <SearchFilter key={index} inputData={data} learningTopics={learningTopics} />;
									})}
								</section>

								<aside className={styles.filterRow}>
									<ClearAll searchFilterData={searchFilterData} text={props.fields.ClearFiltersText} />
								</aside>
							</Accordion>
						</div>
						<input type="hidden" defaultValue={pageIndex} {...formMethods.register('PageIndex')} />{' '}
					</form>

					{!params.q && totalResults > 0 && <p className={styles.resultsText + ' padX'}>{generalResultsText}</p>}

					{params.q && (
						<p className={styles.resultsText + ' padX'}>
							{keywordResultsText} <strong>&quot;{params.q}&quot;</strong>
							{!spellingSuggestion ? '.' : ' '}
							{spellingSuggestion && (
								<em>
									- did you mean{' '}
									<button type="button" onClick={() => formMethods.setValue('Keyword', spellingSuggestion)} className={styles.didYouMean}>
										{spellingSuggestion}
									</button>
									?
								</em>
							)}
						</p>
					)}

					{totalResults > 1 && (
						<SortByFilter defaultSortOption={params.sortOption} label={props.fields.SortByText} inputData={sortByData()} />
					)}
					{searchState == FetchState.LOADING && !searchResult?.length ? (
						<SearchListingLoading />
					) : (
						(searchState === FetchState.WARNING || isEditing || totalResults === 0) && (
							<Text className={styles.warning} tag="h3" field={props.fields.NoResultsFoundText} />
						)
					)}
				</FormProvider>

				{totalResults > 0 && (
					<Listing
						items={[...previousResults, ...searchResult]}
						filters={filterGroups}
						wildCardUrl={props.fields.WildCardConfig?.fields?.SearchListingWildcardPage?.value?.href}
						favouriteButtonText={props.fields.FavouriteButtonText}
						unfavouriteText={props.fields.UnfavouriteText}
						registeredUserText={props.fields.RegisteredUserText}
						rcnMemberText={props.fields.RCNMemberText}
						rcniPlusSubscriberText={props.fields.RCNiPlusSubscriberText}
						organisationalProductText={props.fields.OrganisationalProductText}
						openAccessText={props.fields.OpenAccessText}
						subjectLinkPrependedText={props.fields.SubjectLinkPrependedText}
						shareButtonToolTip={props.fields.ShareButtonToolTip}
						shareOptions={props.fields.ShareOptions}
						noShareOptions={props.fields.NoShareOptions}
						setSubjectValue={(name: 'LearningTopics', value: string) => {
							formMethods.setValue(name, [value]);

							window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
						}}
						cpdHoursText={props.fields.CPDHoursText}
					/>
				)}

				<ShowMore
					currentText={props.fields.CurrentlyShowingText}
					totalResults={totalResults}
					pageIndex={pageIndex}
					pageSize={params.pageSize}
				>
					<Text
						tag="button"
						onClick={nextPage}
						field={props.fields.ShowMoreText}
						className={`${styles.button} link linkOffset linkHoverWeighted`}
					/>
				</ShowMore>
			</section>
		</>
	);
}
